import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle, Camera, AlertCircle, Shield, Package, Mail, Download, ArrowLeft } from "lucide-react";
import { CameraCapture } from "@/components/CameraCapture";
import { useToast } from "@/hooks/use-toast";

interface Box {
  id: string;
  box_number: string;
  name: string;
  has_exterior_photo: boolean;
  shipping_label_url: string | null;
  tracking_number: string | null;
}

interface Shipment {
  id: string;
  pickup_date: string;
  pickup_time_slot: string;
  box_ids: string[];
}

const SchedulePickupSuccess = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const shipmentId = searchParams.get('shipment_id');
  const isPrepaid = searchParams.get('prepaid') === 'true';
  
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailingLabels, setEmailingLabels] = useState(false);
  const [downloadingLabels, setDownloadingLabels] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [currentBoxId, setCurrentBoxId] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId || shipmentId) {
      fetchPickupDetails();
    }
  }, [sessionId, shipmentId]);

  const fetchPickupDetails = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }

      // Fetch recent shipments to get box IDs
      const { data: shipmentData } = await supabase
        .from('shipments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!shipmentData?.box_ids) {
        throw new Error('No shipment found');
      }

      setShipment(shipmentData);

      // Fetch boxes from the shipment
      const { data: boxesData, error: boxesError } = await supabase
        .from('boxes')
        .select('id, box_number, name, has_exterior_photo, shipping_label_url, tracking_number')
        .in('id', shipmentData.box_ids);

      if (boxesError) throw boxesError;

      setBoxes(boxesData || []);
    } catch (error) {
      console.error('Error fetching pickup details:', error);
      toast({
        title: "Error",
        description: "Failed to load pickup details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabels = async () => {
    setDownloadingLabels(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke('generate-shipping-labels', {
        body: { 
          box_ids: boxes.map(b => b.id),
          user_id: user.id
        }
      });

      if (error) throw error;

      // Check for incomplete profile error
      if (data?.error === 'incomplete_profile') {
        const missingFieldsMap: Record<string, string> = {
          'valid_phone': 'Valid phone number (currently using placeholder)',
          'phone': 'Phone number',
          'address_line1': 'Street address',
          'city': 'City',
          'state': 'State',
          'zip': 'ZIP code',
          'full_name': 'Full name'
        };

        const missingFields = data.missing_fields || [];
        const fieldsList = missingFields
          .map((field: string) => missingFieldsMap[field] || field)
          .join('\n• ');

        toast({
          title: "Profile Incomplete",
          description: (
            <div>
              <p className="mb-2">Please update your profile with the following information:</p>
              <p className="mb-3 pl-2">• {fieldsList}</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/settings')}
                className="mt-2"
              >
                Go to Settings
              </Button>
            </div>
          ),
          variant: "destructive",
          duration: 10000,
        });
        return;
      }

      // Refresh boxes to get updated label URLs
      await fetchPickupDetails();

      toast({
        title: "Labels Generated",
        description: "Shipping labels are ready to download",
      });
    } catch (error: any) {
      console.error('Error generating labels:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate shipping labels. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingLabels(false);
    }
  };

  const handleEmailLabels = async () => {
    if (!shipment) return;
    
    setEmailingLabels(true);
    try {
      const { data, error } = await supabase.functions.invoke('email-shipping-labels', {
        body: { shipmentId: shipment.id }
      });

      if (error) throw error;

      toast({
        title: "Labels Sent",
        description: "Shipping labels have been sent to your email",
      });
    } catch (error: any) {
      console.error('Error emailing labels:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to email labels. Please try again.",
        variant: "destructive",
      });
    } finally {
      setEmailingLabels(false);
    }
  };

  const handleTakePhoto = (boxId: string) => {
    setCurrentBoxId(boxId);
    setShowCamera(true);
  };

  const handlePhotoCapture = async (imageData: string) => {
    if (!currentBoxId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Convert base64 to blob
      const base64Data = imageData.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });

      const fileName = `${user.id}/${currentBoxId}/${Date.now()}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('box-photos')
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      // Create photo record with exterior type
      const { data: newPhoto, error: photoError } = await supabase
        .from('photos')
        .insert({
          box_id: currentBoxId,
          storage_path: fileName,
          photo_type: 'exterior',
        })
        .select()
        .single();

      if (photoError) throw photoError;

      // Trigger AI classification
      await supabase.functions.invoke('classify-box-photo', {
        body: { photo_id: newPhoto.id, box_id: currentBoxId }
      });

      // Update local state
      setBoxes(prev => prev.map(box => 
        box.id === currentBoxId 
          ? { ...box, has_exterior_photo: true }
          : box
      ));

      setShowCamera(false);
      setCurrentBoxId(null);

      toast({
        title: "Photo uploaded",
        description: "Insurance coverage activated for this box",
      });
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: "Error",
        description: "Failed to upload photo",
        variant: "destructive",
      });
    }
  };

  const boxesWithoutPhotos = boxes.filter(b => !b.has_exterior_photo);
  const allPhotosComplete = boxesWithoutPhotos.length === 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle p-4 md:p-6">
      <header className="mb-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold text-primary">Endless Storage</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to Dashboard</span>
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Success Message */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <h1 className="text-2xl font-bold">Pickup Scheduled!</h1>
                <p className="text-muted-foreground">Your boxes are ready to ship</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insurance Coverage Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Shipping Insurance Coverage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert className={allPhotosComplete ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                {allPhotosComplete 
                  ? "All boxes are insured ($100 per box)"
                  : "Add exterior photos to activate insurance"
                }
              </AlertTitle>
              <AlertDescription>
                {allPhotosComplete
                  ? "You're fully covered! Your boxes have $100 shipping insurance."
                  : `${boxesWithoutPhotos.length} box(es) need exterior photos for insurance coverage.`
                }
              </AlertDescription>
            </Alert>

            {!allPhotosComplete && (
              <div className="mt-4 space-y-3">
                {boxesWithoutPhotos.map(box => (
                  <div key={box.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{box.name}</div>
                      <div className="text-sm text-muted-foreground">{box.box_number}</div>
                      <div className="text-xs text-muted-foreground mt-1">Include box label in photo</div>
                    </div>
                    <Button onClick={() => handleTakePhoto(box.id)} size="sm">
                      <Camera className="h-4 w-4 mr-2" />
                      Take Photo
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shipping Labels */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Shipping Labels
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertDescription>
                Your shipping labels will be emailed to you within 15 minutes. 
                You can also download them below or request them via email.
              </AlertDescription>
            </Alert>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="default" 
                className="flex-1 gap-2"
                onClick={handleDownloadLabels}
                disabled={downloadingLabels}
              >
                <Download className="h-4 w-4" />
                {downloadingLabels ? "Generating..." : "Download Labels (PDF)"}
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 gap-2"
                onClick={handleEmailLabels}
                disabled={emailingLabels}
              >
                <Mail className="h-4 w-4" />
                {emailingLabels ? "Sending..." : "Email Me Labels"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card>
          <CardHeader>
            <CardTitle>Before Your Pickup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Print labels and attach them to your boxes</li>
              <li>Seal your boxes securely with packing tape</li>
              <li>Have boxes ready in your lobby or by the door</li>
              <li>Driver will call 15 minutes before arrival</li>
              <li>Track shipment using tracking numbers from email</li>
              <li>Boxes arrive at our facility within 3-5 business days</li>
            </ol>
          </CardContent>
        </Card>

        {/* Questions */}
        <Card>
          <CardHeader>
            <CardTitle>Questions?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              If you have any questions or need assistance, please reach out to our support team:
            </p>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Mail className="h-4 w-4 text-primary" />
              <a 
                href="mailto:support@endless-storage.com"
                className="text-sm font-medium text-primary hover:underline"
              >
                support@endless-storage.com
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      <CameraCapture
        open={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handlePhotoCapture}
      />
    </div>
  );
};

export default SchedulePickupSuccess;
